import type { MarketMaker, MarketMakerInput } from '@/features/mm-programs/mm-program-schema';
import { bopApi } from '@/lib/api/client';
import { type AuditEntry, type RawAuditRecord, toAuditEntries } from './user-audit';

const MM_PROGRAMS_PATH = 'v1/mm-programs';
const AUDIT_PREFIX = 'mm_program_';

export type ScheduleChangeKind = 'starts' | 'ends' | 'changes';

interface ScheduleChange {
  date: string;
  kind: ScheduleChangeKind;
}

export interface InstrumentSummary {
  instrument: string;
  live: boolean;
  type: string | null;
  sessioned: boolean;
  monthlyCompensationUsd: number;
  since: string | null;
  /** Exclusive end of the schedule in force; null when open */
  until: string | null;
  nextChange: ScheduleChange | null;
}

export interface MmProgramSummary {
  p: string;
  name: string;
  fast_id: number;
  active: boolean;
  email: string;
  /** Optimistic-lock version; 0 for documents never saved through bop-api */
  version: number;
  instruments: InstrumentSummary[];
  liveInstruments: number;
  /** Monthly USD at full KPI across the schedules in force today */
  monthlyCompensationUsd: number;
  nextChange: (ScheduleChange & { instrument: string }) | null;
  /** Why the scoring job would reject the stored document; it refuses to run while any document has one */
  problems: string[];
}

export interface UnconfiguredMarketMaker {
  fastId: number;
  /** null until the account's QuestDB MM row has been backfilled with it */
  pid: string | null;
  name: string;
}

export interface GetMmProgramsResponse {
  asOf: string;
  canEdit: boolean;
  marketMakers: MmProgramSummary[];
  unconfigured: UnconfiguredMarketMaker[];
  /** False when QuestDB could not be read, so `unconfigured` is unknown rather than empty */
  accountsAvailable: boolean;
}

/** As stored: documents written outside bop-api may not pass the settings schema yet. */
export interface GetMmProgramResponse {
  marketMaker: MarketMakerInput;
  version: number;
  canEdit: boolean;
}

interface UpdateMmProgramRequest {
  comment: string;
  /** The version the edit started from; bop-api answers 409 when someone saved since */
  expectedVersion: number;
  marketMaker: MarketMaker;
}

export interface SaveMmProgramResponse {
  marketMaker: MarketMaker;
  version: number;
  auditWritten: boolean;
}

export const getMmPrograms = () => bopApi.get(MM_PROGRAMS_PATH).json<GetMmProgramsResponse>();

export const getMmProgram = (pid: string) => bopApi.get(`${MM_PROGRAMS_PATH}/${encodeURIComponent(pid)}`).json<GetMmProgramResponse>();

export const updateMmProgram = (pid: string, request: UpdateMmProgramRequest) =>
  bopApi.put(`${MM_PROGRAMS_PATH}/${encodeURIComponent(pid)}`, { json: request }).json<SaveMmProgramResponse>();

/** bop-api builds the settings from the account: FAST ID, institution name and email. */
export const createMmProgram = (pid: string) => bopApi.post(MM_PROGRAMS_PATH, { json: { pid } }).json<SaveMmProgramResponse>();

export const getMmProgramHistory = async (pid: string): Promise<AuditEntry[]> =>
  toAuditEntries(await bopApi.get(`v1/audit/${encodeURIComponent(pid)}`, { searchParams: { includePrefixes: AUDIT_PREFIX } }).json<RawAuditRecord[]>());

interface MmProgramDowntimeWindow {
  start: string;
  end: string;
  ruleIds: string[];
}

export interface GetMmProgramDowntimeResponse {
  instrument: string;
  from: string;
  to: string;
  windows: MmProgramDowntimeWindow[];
  downtimeMs: number;
}

/** The stored downtime rules for one instrument resolved into merged UTC windows over `[from, to)`. */
export const getMmProgramDowntime = (pid: string, params: { instrument: string; from: string; to: string }) =>
  bopApi.get(`${MM_PROGRAMS_PATH}/${encodeURIComponent(pid)}/downtime`, { searchParams: params }).json<GetMmProgramDowntimeResponse>();

/** An index price feed that published a bid and ask recently, and the exchange instruments it prices. */
interface QuoteSource {
  provider: string;
  symbol: string;
  instruments: string[];
  lastSeen: string;
}

interface GetQuoteSourcesResponse {
  hours: number;
  sources: QuoteSource[];
}

export const getQuoteSources = () => bopApi.get(`${MM_PROGRAMS_PATH}/quote-sources`).json<GetQuoteSourcesResponse>();
